import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { useNavigate, useParams } from "react-router-dom";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import CredentialTemplateCard from "../../components/Cards/CredentialTemplateCard/CredentialTemplateCard";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import CfLoader from "../../common/CfLoader/CfLoader";
import Toast from "../../common/Toast/Toast";
import { useTranslation } from "react-i18next";

const AllCertificatesPerCategory = () => {
  const { category_name, category_id } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const [limit, setLimit] = useState(0);
  const [offset, setOffset] = useState(0);
  const [contextList, setContextList] = useState([]);
  const [count, setCount] = useState(0);
  const [categoryName, setCategoryName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [hasLoadedCategoryData, setHasLoadedCategoryData] = useState(false);

  const containerRef = useRef(null);
  const cardWidth = "19.125rem";
  const gap = "0.75rem";

  // Function to handle template card click
  const handleTemplateClick = (credential_template_id) => {
    if (authContext.isLoggedIn) {
      // User is logged in, navigate to the edit page
      navigate(`/certificate/edit/${credential_template_id}`);
    } else {
      // User is logged out, open login modal
      authContext.setTriggerLoginModuleToggleValue(true);
      // Store the target URL to redirect after login
      sessionStorage.setItem(
        "redirectAfterLogin",
        `/certificate/edit/${credential_template_id}`
      );
    }
  };

  const GET_CERTIFICATES_LIST = useCallback(
    async (currentOffset) => {
      if (
        isLoading ||
        isLoadingMore ||
        (currentOffset > 0 && contextList.length >= count)
      ) {
        return;
      }

      if (currentOffset === 0) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const response = await GET(
          `${EntityConstants.TEMPLATE_PER_CATEGORY}category_id=${category_id}&limit=${limit}&offset=${currentOffset}`
        );
        const data = await response.json();

        if (!response.ok || data.error) {
          const errorMessage =
            data.error || "Failed to fetch templates. Please try again later.";

          Toast({
            type: "error",
            title: "Error Loading Certificates",
            message: errorMessage,
          });
          navigate("/designs");
          return;
        }

        setCount(data.count);
        setContextList((prevList) => [...prevList, ...data.results]);
        setOffset(currentOffset + data.results.length);
        setHasLoadedCategoryData(true);
      } catch (error) {
        console.error("Failed to fetch certificates:", error);

        Toast({
          type: "error",
          title: "Unexpected Error",
          message: "Something went wrong. Please try again later.",
        });
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [
      isLoading,
      isLoadingMore,
      contextList.length,
      count,
      category_id,
      limit,
      navigate,
    ]
  );

  const SEARCH = async (e) => {
    e.preventDefault();
    const searchTerm = e.target.value.trim();
    if (searchTerm.length > 2) {
      setIsLoading(true);
      setIsSearched(true);
      try {
        const response = await GET(
          `${EntityConstants.DISCOVERY_SEARCH}${searchTerm}&category_id=${category_id}`
        );
        const data = await response.json();
        setContextList(data.credentials || []);
      } catch (error) {
        console.error("Error during search:", error);
        setContextList([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsSearched(false);
      setContextList([]);
      setOffset(0);
    }
  };

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculatedCardsPerRow = CalculateCardsPerRow(
          containerRef,
          cardWidth,
          gap
        );

        const cardsToFetch = calculatedCardsPerRow * 4;
        if (cardsToFetch > 0) {
          setLimit(cardsToFetch < 8 ? 8 : cardsToFetch);
        }
      }
    };
    updateCardsPerRow();
  }, [cardWidth, gap]);

  useEffect(() => {
    if (limit > 0 && !isSearched) {
      setContextList([]);
      setOffset(0);
      setHasLoadedCategoryData(false);
      GET_CERTIFICATES_LIST(0);
    }
  }, [limit, isSearched, category_id]);

  useEffect(() => {
    const handleScroll = () => {
      const isAtBottom =
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 200;

      if (
        isAtBottom &&
        !isLoading &&
        !isLoadingMore &&
        !isSearched &&
        contextList.length < count
      ) {
        GET_CERTIFICATES_LIST(offset);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    isLoading,
    isLoadingMore,
    isSearched,
    contextList,
    count,
    offset,
    GET_CERTIFICATES_LIST,
  ]);

  useEffect(() => {
    setCategoryName(category_name.replace(/\+/g, " "));
  }, [category_name]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id={pageRootTestIds.allCertificatesPerCategory}>
      <div className="h-full">
        <div className="bxm cbd font-CfPrimaryFont bg-cfSecondary">
          <div className="flex flex-col items-center w-full mb-4 min-h-screen">
            <div
              className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
            >
              <div
                className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
              >
                <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-center md:self-start my-auto">
                  <button
                    onClick={() => navigate(-1)}
                    id="all-certificates-per-category-back"
                    className="flex items-center justify-center focus:outline-none cursor-pointer"
                    aria-label="Go back"
                  >
                    <img
                      src={ArrowLeftIcon}
                      alt=""
                      className="cursor-pointer"
                    />
                  </button>
                  <p>{categoryName}</p>
                </div>
                <form className="bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center ml-[0.630rem] md:self-end md:m-0">
                  <input
                    type="text"
                    name="search"
                    id="all-certificates-per-category-input-search"
                    onChange={SEARCH}
                    placeholder={`${t("designs:search_in")} ${categoryName}`}
                    className="outline-none border-none p-0 placeholder:text-sm w-full"
                    style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  />
                </form>
              </div>
            </div>
            <div ref={containerRef} className="w-full">
              <div className="flex items-center flex-wrap gap-6 w-full p-4">
                {contextList.map(
                  (
                    {
                      credential_template_id,
                      name,
                      description,
                      tags,
                      thumbnail_svg_url,
                    },
                    index
                  ) => (
                    <CredentialTemplateCard
                      key={`${credential_template_id}-${index}`}
                      name={name}
                      description={description}
                      credential_template_id={credential_template_id}
                      tagsList={tags}
                      thumbnail_image={thumbnail_svg_url}
                      onCardClick={() =>
                        handleTemplateClick(credential_template_id)
                      }
                    />
                  )
                )}
              </div>

              {(isLoading || isLoadingMore) && (
                <div className="flex justify-center p-4">
                  <CfLoader />
                </div>
              )}

              {!isLoading &&
                !isLoadingMore &&
                limit > 0 &&
                (isSearched || hasLoadedCategoryData) &&
                contextList.length === 0 && (
                  <p
                    id="all-certificates-per-category-no-results"
                    className="text-grey-50 text-center font-semibold text-lg mt-[1rem] w-full"
                  >
                    {isSearched
                      ? t("designs:no_results")
                      : t("designs:no_templates_in_category")}
                  </p>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCertificatesPerCategory;
